import type { BookingConfirmationSummary } from '@massilia/queries'

import { differenceInMinutes, format, isValid, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Briefcase, Luggage } from 'lucide-react'

import { travelClassTypesLabels } from '@/lib/schemas/flight-research-bar/flight-research-bar-base.schema'
import { formatDuration } from '@/utils/format-duration'

import {
  FlightSegmentCard,
  PassengerCard,
  TicketConditions,
} from '../booking/shared-trip-components'

interface TripRecapSectionProps {
  summary?: BookingConfirmationSummary
}

export function TripRecapSection({ summary }: TripRecapSectionProps) {
  if (!summary || summary.flightSegments.length === 0) return null

  // Aggregate baggage from first segment (same for all segments in a booking)
  const firstSegment = summary.flightSegments[0]
  const checkedLuggageCount = firstSegment.checkedLuggageCount
  const checkedLuggageWeight = firstSegment.checkedLuggageWeight
  const cabinLuggageCount = firstSegment.cabinLuggageCount
  const cabinLuggageWeight = firstSegment.cabinLuggageWeight

  return (
    <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
      <div className="text-gray-light-900 text-lg leading-7 font-semibold sm:text-2xl">
        Récapitulatif de votre voyage
      </div>

      {/* Ticket conditions (en haut) */}
      {summary.conditions && (
        <div className="border-gray-light-200 flex flex-col gap-4 rounded-xl border p-4">
          <div className="text-gray-light-900 text-sm leading-4 font-semibold">
            Conditions du billet
          </div>
          <TicketConditions conditions={summary.conditions} />
        </div>
      )}

      {/* Flight segment cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {summary.flightSegments.map((segment, i) => {
          const departureDateLabel = formatFlightDate(segment.departureAt)
          const arrivalDateLabel = formatFlightDate(segment.arrivalAt)
          const durationLabel = computeDuration(segment.departureAt, segment.arrivalAt)
          const classLabel = segment.travelClass
            ? (travelClassTypesLabels[segment.travelClass as keyof typeof travelClassTypesLabels] ??
              segment.travelClass)
            : 'Économique'

          return (
            <FlightSegmentCard
              key={i}
              airline={segment.airline}
              route={`${segment.departureLocation} vers ${segment.arrivalLocation}`}
              departureLabel={departureDateLabel}
              arrivalLabel={arrivalDateLabel}
              stopsLabel="Direct"
              durationLabel={durationLabel}
              travelClass={classLabel}
            />
          )
        })}
      </div>

      {/* Passengers */}
      {summary.passengers.length > 0 && (
        <div className="border-gray-light-200 flex flex-col gap-4 rounded-xl border bg-white px-4 py-6">
          <div className="text-gray-light-900 text-sm leading-4 font-semibold">
            Détails des voyageurs
          </div>
          {summary.passengers.map((passenger, i) => (
            <PassengerCard
              key={i}
              firstName={passenger.firstName}
              lastName={passenger.lastName}
              gender={passenger.gender}
              birthDate={passenger.birthDate}
            />
          ))}
        </div>
      )}

      {/* Baggage */}
      {(checkedLuggageCount > 0 || cabinLuggageCount > 0) && (
        <div className="border-gray-light-200 flex flex-col gap-4 rounded-xl border p-4">
          <div className="text-gray-light-900 text-sm leading-4 font-semibold">Bagages inclus</div>
          {checkedLuggageCount > 0 && (
            <div className="flex items-start gap-5 pl-4">
              <Luggage className="text-gray-light-700 size-6 shrink-0" />
              <div className="flex flex-col gap-2">
                <div className="text-gray-light-700 text-sm leading-4 font-medium">
                  {checkedLuggageCount} bagage{checkedLuggageCount > 1 ? 's' : ''} soute
                </div>
                <div className="text-gray-light-600 text-xs leading-4 font-medium">
                  Poids maximum : {checkedLuggageWeight > 0 ? `${checkedLuggageWeight} kg` : '– kg'}
                </div>
              </div>
            </div>
          )}
          {cabinLuggageCount > 0 && (
            <div className="flex items-start gap-5 pl-4">
              <Briefcase className="text-gray-light-700 size-6 shrink-0" />
              <div className="flex flex-col gap-2">
                <div className="text-gray-light-700 text-sm leading-4 font-medium">
                  {cabinLuggageCount} bagage{cabinLuggageCount > 1 ? 's' : ''} cabine
                </div>
                <div className="text-gray-light-600 text-xs leading-4 font-medium">
                  Poids maximum : {cabinLuggageWeight > 0 ? `${cabinLuggageWeight} kg` : '– kg'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  )
}

// ── Sub-components ──

function computeDuration(departureAt: string, arrivalAt: string): string {
  const dep = parseISO(departureAt)
  const arr = parseISO(arrivalAt)
  if (!isValid(dep) || !isValid(arr)) return '–'

  const totalMinutes = differenceInMinutes(arr, dep)
  return formatDuration(totalMinutes)
}

// ── Helpers ──

function formatFlightDate(isoDate: string): string {
  const date = parseISO(isoDate)
  if (!isValid(date)) return isoDate
  const formatted = format(date, "EEE d MMM 'à' H'h'mm", { locale: fr })
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}
